import { z } from 'zod';
import { dateOrTimeSchema } from './content-primitives.ts';
import { validateJsonLdValues } from './json-ld-values.ts';
import { jsonValueSchema, type JsonValue } from './json.ts';
import { organizationTypes } from './schema-types.ts';

export type JsonLdObject = Record<string, JsonValue> & { '@id'?: string; '@type'?: string | string[] };
export const jsonLdObjectSchema: z.ZodType<JsonLdObject> = z
  .object({
    '@id': z.string().optional(),
    '@type': z.union([z.string(), z.array(z.string()).nonempty()]).optional(),
  })
  .catchall(jsonValueSchema)
  .superRefine((node, ctx) => {
    if (!node['@type'] && !node['@id'])
      ctx.addIssue({ code: 'custom', message: 'An object needs @type or an @id reference.' });
    if ('@context' in node)
      ctx.addIssue({ code: 'custom', message: 'The page supplies the Schema.org context.' });
  });
export const additionalObjectsSchema = z
  .array(jsonLdObjectSchema)
  .max(200)
  .describe(
    'Schema.org JSON-LD objects in this page’s language. Use @type, native property names, and optional @id references. The page supplies @context.',
  );
export function jsonLdTypes(node: JsonLdObject): string[] {
  return (Array.isArray(node['@type']) ? node['@type'] : node['@type'] ? [node['@type']] : []).map((type) =>
    type.replace(/^https?:\/\/schema\.org\//, ''),
  );
}
export const siteOrganizationSchema = jsonLdObjectSchema.superRefine((node, ctx) => {
  if (!jsonLdTypes(node).some((type) => (organizationTypes as readonly string[]).includes(type)))
    ctx.addIssue({ code: 'custom', path: ['@type'], message: 'Choose an Organization type.' });
  if (node['@id'] !== undefined && node['@id'] !== '/#identity')
    ctx.addIssue({ code: 'custom', path: ['@id'], message: 'The site organization uses /#identity.' });
});
const authorObjectsSchema = z
  .array(jsonLdObjectSchema)
  .max(100)
  .superRefine((authors, ctx) => {
    authors.forEach((author, i) => {
      if (
        author['@type'] &&
        !jsonLdTypes(author).some(
          (type) => type === 'Person' || (organizationTypes as readonly string[]).includes(type),
        )
      )
        ctx.addIssue({
          code: 'custom',
          path: [i, '@type'],
          message: 'An author must be a person or organization.',
        });
    });
  });
export const siteAuthorsSchema = authorObjectsSchema.superRefine((authors, ctx) => {
  const ids = new Set<string>();
  for (const [index, author] of authors.entries()) {
    const id = author['@id'];
    if (!id || !/^\/#[^\s#]+$/.test(id) || ['/#identity', '/#website'].includes(id))
      ctx.addIssue({
        code: 'custom',
        path: [index, '@id'],
        message: 'Use a unique site-wide ID such as /#jane-smith.',
      });
    else if (ids.has(id))
      ctx.addIssue({ code: 'custom', path: [index, '@id'], message: 'Author IDs must be unique.' });
    if (id) ids.add(id);
    if (!author['@type'])
      ctx.addIssue({
        code: 'custom',
        path: [index, '@type'],
        message: 'Choose Person or an Organization type.',
      });
  }
});
export const pageObjectSchema = z.record(z.string(), jsonValueSchema).superRefine((node, ctx) => {
  if (node.author !== undefined) {
    const authors = authorObjectsSchema.safeParse(Array.isArray(node.author) ? node.author : [node.author]);
    if (!authors.success)
      for (const issue of authors.error.issues)
        ctx.addIssue({ code: 'custom', path: ['author', ...issue.path], message: issue.message });
  }
  const result = jsonLdObjectSchema.safeParse({ '@type': 'WebPage', ...node });
  if (!result.success)
    for (const issue of result.error.issues)
      ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
});
export function jsonLdObjects(value: unknown): JsonLdObject[] {
  if (Array.isArray(value)) return value.flatMap(jsonLdObjects);
  if (!value || typeof value !== 'object') return [];
  const node = value as JsonLdObject;
  return [node, ...Object.values(node).flatMap(jsonLdObjects)];
}
export function validatePageJsonLd(
  data: JsonLdObject[],
  authorDefinitions: JsonLdObject[],
  pageUrl: string,
  siteIds: ReadonlyMap<string, string[]> = new Map([
    ['/#identity', ['Organization']],
    ['/#website', ['WebSite']],
  ]),
  pageObject?: JsonLdObject,
): Array<{ field: string; message: string }> {
  const errors: Array<{ field: string; message: string }> = [];
  const definitions = new Map<string, string[]>();
  const references: Array<{ id: string; field: string; property?: string }> = [];
  const key = (id: string) => new URL(id, pageUrl).href;
  const siteDefinitions = new Map([...siteIds].map(([id, types]) => [key(id), types]));
  const visit = (
    value: JsonValue | undefined,
    field: string,
    property?: string,
    parent?: JsonLdObject,
  ): void => {
    if (Array.isArray(value)) {
      value.forEach((entry, i) => visit(entry, `${field}.${i}`, property, parent));
      return;
    }
    if (!value || typeof value !== 'object') return;
    const node = value as JsonLdObject;
    if (node['@id'] !== undefined) {
      if (typeof node['@id'] !== 'string' || !node['@id'].trim() || /\s/.test(node['@id'])) {
        errors.push({ field, message: 'Use a valid @id.' });
        return;
      }
      let id: string;
      try {
        id = key(node['@id']);
      } catch {
        errors.push({ field, message: 'Use a valid @id.' });
        return;
      }
      const reference = Object.keys(node).every((key) => key === '@id');
      if (reference) references.push({ id, field, property });
      else {
        if (definitions.has(id) || siteDefinitions.has(id))
          errors.push({ field, message: 'This @id is already defined.' });
        definitions.set(id, jsonLdTypes(node));
      }
    }
    if (
      node['@type'] !== undefined &&
      !(typeof node['@type'] === 'string' && node['@type'].trim()) &&
      !(
        Array.isArray(node['@type']) &&
        node['@type'].length &&
        node['@type'].every((type) => typeof type === 'string' && type.trim())
      )
    )
      errors.push({ field: `${field}.@type`, message: 'Use a type name or an array of type names.' });
    if ('@context' in node) errors.push({ field, message: 'The page supplies the Schema.org context.' });
    errors.push(...validateJsonLdValues(node, field, property, parent));
    if (node.answerCount !== undefined) {
      const count = Number(node.answerCount),
        answers = [node.acceptedAnswer, node.suggestedAnswer].flatMap((value) =>
          value == null ? [] : Array.isArray(value) ? value : [value],
        );
      if (!Number.isInteger(count) || count < answers.length)
        errors.push({
          field: `${field}.answerCount`,
          message: 'The answer count must include the supplied answers.',
        });
    }
    if (node['@type'] === 'ProductGroup' && Array.isArray(node.hasVariant)) {
      const identifiers = new Set<string>();
      for (const variant of node.hasVariant)
        if (variant && typeof variant === 'object' && !Array.isArray(variant)) {
          if (
            node.productGroupID &&
            variant.inProductGroupWithID &&
            node.productGroupID !== variant.inProductGroupWithID
          )
            errors.push({ field, message: 'Variants must use their group’s identifier.' });
          const ids = [
            variant.sku && `sku:${variant.sku}`,
            ...['gtin', 'gtin8', 'gtin12', 'gtin13', 'gtin14'].flatMap((key) =>
              typeof variant[key] === 'string' ? [`gtin:${variant[key].padStart(14, '0')}`] : [],
            ),
          ].filter((id): id is string => typeof id === 'string');
          if (ids.some((id) => identifiers.has(id)))
            errors.push({ field, message: 'Product variants must not share a SKU or GTIN.' });
          ids.forEach((id) => identifiers.add(id));
        }
    }
    for (const [name, child] of Object.entries(node)) {
      const values = Array.isArray(child) ? child : [child];
      if (['url', 'contentUrl', 'embedUrl', 'license', 'acquireLicensePage', 'sameAs'].includes(name))
        for (const value of values)
          if (typeof value === 'string') {
            try {
              if (/\s/.test(value) || !['http:', 'https:'].includes(new URL(value, pageUrl).protocol))
                throw Error();
            } catch {
              errors.push({ field: `${field}.${name}`, message: 'Use a valid URL.' });
            }
          }
      if (
        ['startDate', 'endDate', 'datePublished', 'dateModified', 'datePosted', 'uploadDate'].includes(
          name,
        ) &&
        typeof child === 'string' &&
        !dateOrTimeSchema.safeParse(child).success
      )
        errors.push({ field: `${field}.${name}`, message: 'Use an ISO date or date/time.' });
      if (
        ['name', 'description', 'text', 'reviewBody'].includes(name) &&
        values.some(
          (value) =>
            typeof value !== 'string' &&
            !(value && typeof value === 'object' && !Array.isArray(value) && '@value' in value),
        )
      )
        errors.push({ field: `${field}.${name}`, message: 'Use text in this page’s language.' });
    }
    if (
      typeof node.startDate === 'string' &&
      typeof node.endDate === 'string' &&
      Date.parse(node.endDate) < Date.parse(node.startDate)
    )
      errors.push({ field, message: 'The end date must follow the start date.' });
    if (node.ratingValue !== undefined) {
      const value = Number(node.ratingValue),
        minimum = Number(node.worstRating ?? 1),
        maximum = Number(node.bestRating ?? 5);
      if (!Number.isFinite(value) || value < minimum || value > maximum || maximum <= minimum)
        errors.push({ field, message: 'The rating must fall within its minimum and maximum scale.' });
    }

    for (const [name, child] of Object.entries(node)) visit(child, `${field}.${name}`, name, node);
  };
  if (pageObject) visit(pageObject, 'pageObject');
  data.forEach((node, i) => visit(node, `additionalObjects.${i}`));
  authorDefinitions.forEach((node, i) => visit(node, `pageObject.author.${i}`, 'author'));
  for (const reference of references) {
    const target = definitions.get(reference.id) ?? siteDefinitions.get(reference.id);
    if (!target)
      errors.push({
        field: reference.field,
        message: 'This reference does not identify an object in this page or the site organization.',
      });
    else if (
      ['author', 'authors', 'publisher', 'creator', 'hiringOrganization'].includes(
        reference.property ?? '',
      ) &&
      !target.some((type) => type === 'Person' || (organizationTypes as readonly string[]).includes(type))
    )
      errors.push({
        field: reference.field,
        message: 'This reference must identify a person or organization.',
      });
  }
  return errors;
}
